/**
 * Notification centre bell for the top bar.
 *
 * This is the batteries-included `NotificationBell` from
 * `@authe/notification-center` fed by the shared `ApiClient` notification source
 * (`useNotificationSource` — the `/notification_history` resource + live push).
 * The package owns the unread badge, mark-all-read, per-criticality list and
 * live high-severity toasts; we only inject the data port and the "view all"
 * route, so the bell and the Activity feed read the same backend with no
 * duplicated fetch code.
 */
import { useNavigate } from "@tanstack/react-router";
import { NotificationBell, type Notification } from "@authe/notification-center";
import { eventRoute } from "@/features/activity/eventRoute";
import { useNotificationPresenter, useNotificationSource } from "@/api/adapters";
import { useNotificationsReadKey } from "@/app/notificationsReadKey";
import { ROUTES } from "@/config";

export function NotificationsMenu(): React.ReactElement {
  const navigate = useNavigate();
  const source = useNotificationSource();
  const present = useNotificationPresenter();
  // Per PERSON, not per browser. This was the literal
  // `"dashboard.notifications.read"`, one keyspace for everyone who signs in
  // here: on a shared analyst workstation the second analyst's bell showed the
  // first analyst's critical alerts as already read, and left them out of the
  // unread badge. Server read-state is posted but never read back, so this key
  // is the only record there is.
  const readKey = useNotificationsReadKey();

  // Deep-link a notification to the resource it's about — the group it
  // completed, the tracer that ran, the watch that was created (see
  // `eventRoute`, shared with the Activity page). Only an event whose subject
  // has no page of its own falls back to the Activity feed.
  const onSelect = (notification: Notification): void => {
    const route = eventRoute(notification.event);
    navigate((route ?? { to: ROUTES.activity }) as never);
  };

  return (
    <NotificationBell
      // The controller seeds its read-id set from `persistKey` ONCE, on mount,
      // so a key that changes has to remount it — otherwise a session resolving
      // after first paint leaves the bell counting against the wrong keyspace.
      key={readKey}
      withTooltip
      source={source}
      present={present}
      persistKey={readKey}
      onSelect={onSelect}
      onViewAll={() => navigate({ to: ROUTES.activity })}
    />
  );
}
